import { TMasterLanguageTestLevel } from "@/api/entities/master-language-test.entity";
import { createLanguageTestLevelApi, updateLanguageTestLevelApi } from "@/api/services/global/master";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { languageTestLevelSchema } from "@/features/master-data/schema/masterDataLanguageTestSchema";
import { TLanguageTestLevelForm } from "@/features/master-data/types/masterDataLanguageTestForm";
import { setErrorForm, showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useMemo } from "react";
import { useForm } from "react-hook-form";

type TLanguageTestLevelFormModalProps = {
  onClose: () => void;
  isOpen: boolean;
  selectedLevel?: TMasterLanguageTestLevel;
  existingLanguageTestId: number;
  existingLanguageTestName: string;
  existingDescription: string;
  setSelectedLevelRows: React.Dispatch<React.SetStateAction<TMasterLanguageTestLevel[]>>;
};

export const LanguageTestLevelFormModal: FC<TLanguageTestLevelFormModalProps> = ({
  onClose,
  isOpen,
  selectedLevel,
  existingLanguageTestId,
  existingLanguageTestName,
  existingDescription,
  setSelectedLevelRows,
}) => {
  const queryClient = useQueryClient();

  const defaultValues = useMemo(
    () => ({
      level: selectedLevel ? selectedLevel.level : "",
      order: selectedLevel ? selectedLevel.order : 1,
      isHidden: selectedLevel ? selectedLevel.isHidden : false,
    }),
    [selectedLevel],
  );

  const methods = useForm<TLanguageTestLevelForm>({
    resolver: yupResolver(languageTestLevelSchema),
    mode: "onBlur",
    defaultValues,
  });

  const { control } = methods;

  const onSubmit = async (data: TLanguageTestLevelForm) => {
    try {
      if (selectedLevel) {
        const rs = await updateLanguageTestLevelApi(existingLanguageTestId, selectedLevel.id, {
          level: data.level,
        });
        setSelectedLevelRows((prev) => {
          const arr = prev.map((item) => (toNumber(item.id) === toNumber(rs.data.id) ? rs.data : item));
          return arr;
        });
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      } else {
        const rs = await createLanguageTestLevelApi(existingLanguageTestId, {
          level: data.level,
        });
        setSelectedLevelRows((prev) => [...prev, rs.data]);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      }
      onClose();
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GLOBAL.MASTER_LANGUAGE_TEST] });
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Modal width={760} title={selectedLevel ? "編集" : "追加"} isOpen={isOpen} enableFooter={false} onClose={onClose} enableCloseIcon={false}>
      <Form methods={methods} onSubmit={onSubmit} schema={languageTestLevelSchema}>
        <Grid container spacing={3} py={3}>
          <Grid item xs={6}>
            <FormField label="試験名">
              <Typography variant="body14">{existingLanguageTestName}</Typography>
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField label="説明">
              <Typography variant="body14">{existingDescription}</Typography>
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="level">
              <Input />
            </FormField>
          </Grid>
        </Grid>
        <Stack flexDirection="row" justifyContent="center" gap="24px">
          <Button variant="outline" onClick={onClose}>
            キャンセル
          </Button>
          <Button type="submit">保存</Button>
        </Stack>
      </Form>
    </Modal>
  );
};
